import axios from 'axios';
import { config } from '../config/config';

class AirtableService {
  constructor() {
    this.baseUrl = `https://api.airtable.com/v0/${config.airtable.baseId}`;
    this.headers = {
      'Authorization': `Bearer ${config.airtable.apiKey}`,
      'Content-Type': 'application/json'
    };

    // In-memory cache for API responses
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get cached data or fetch if not available
   * @param {string} cacheKey - Unique key for this request
   * @param {Function} fetchFn - Function that returns a Promise with the data
   */
  async getCached(cacheKey, fetchFn) {
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    const data = await fetchFn();

    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });

    return data;
  }

  /**
   * Clear the entire cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Clear a specific cache entry
   */
  clearCacheKey(key) {
    this.cache.delete(key);
  }

  /**
   * Fetch all job positions with their form mappings
   * Handles pagination to fetch all records (Airtable returns max 100 per request)
   */
  async getPositions() {
    return this.getCached('positions', async () => {
      try {
        const tableName = encodeURIComponent(config.airtable.tables.positions);
        let allRecords = [];
        let offset = null;

        // Fetch all pages
        do {
          const url = `${this.baseUrl}/${tableName}?fields%5B%5D=Title&fields%5B%5D=Job%20Form&sort%5B0%5D%5Bfield%5D=Title&sort%5B0%5D%5Bdirection%5D=asc${offset ? `&offset=${offset}` : ''}`;
          const response = await axios.get(url, { headers: this.headers });

          allRecords = allRecords.concat(response.data.records);
          offset = response.data.offset; // Airtable provides offset for next page

        } while (offset);

        // Filter out 'All Positions' option (case-insensitive)
        const filteredRecords = allRecords.filter(record => {
          const title = record.fields.Title;
          if (!title) return false;
          const titleLower = title.trim().toLowerCase();
          return titleLower !== 'all positions';
        });

        return filteredRecords.map(record => ({
          id: record.id,
          title: record.fields.Title,
          jobForm: record.fields['Job Form']
        }));
      } catch (error) {
        console.error('Error fetching positions:', error.response?.data || error.message);
        return [];
      }
    });
  }

  /**
   * Fetch all countries with their IDs
   * Uses comprehensive countries.json data (152 countries) as primary source
   * Falls back to Airtable if JSON import fails
   */
  async getCountries() {
    return this.getCached('countries', async () => {
      try {
        // Import countries from JSON file
        const countriesData = await import('../data/countries.json');
        const countryNames = Object.keys(countriesData.default || countriesData);

        // Convert to format expected by the form
        return countryNames.sort().map((countryName, index) => ({
          id: `country_${index}`, // Generate consistent IDs
          name: countryName
        }));
      } catch (jsonError) {
        console.warn('Failed to load countries.json, falling back to Airtable:', jsonError.message);

        // Fallback to Airtable if JSON fails
        try {
          const tableName = encodeURIComponent(config.airtable.tables.countries);
          let allRecords = [];
          let offset = null;

          // Fetch all pages
          do {
            const url = `${this.baseUrl}/${tableName}?sort%5B0%5D%5Bfield%5D=Country&sort%5B0%5D%5Bdirection%5D=asc${offset ? `&offset=${offset}` : ''}`;
            const response = await axios.get(url, { headers: this.headers });

            allRecords = allRecords.concat(response.data.records);
            offset = response.data.offset;
          } while (offset);

          return allRecords.map(record => ({
            id: record.id,
            name: record.fields.Country || record.fields.Name || record.fields.name
          }));
        } catch (airtableError) {
          console.error('Error fetching countries from Airtable:', airtableError.response?.data || airtableError.message);
          return [];
        }
      }
    });
  }

  /**
   * Fetch common field options from ATS table metadata
   */
  async getCommonFieldOptions() {
    return this.getCached('commonFieldOptions', async () => {
      try {
        // Get table schema to extract field choices
        const schemaUrl = `https://api.airtable.com/v0/meta/bases/${config.airtable.baseId}/tables`;
        const schemaResponse = await axios.get(schemaUrl, { headers: this.headers });

        const atsTable = schemaResponse.data.tables.find(t => t.name === config.airtable.tables.ats);
        if (!atsTable) return {};

      // Extract select field options
      const options = {};

      const hiringSourceField = atsTable.fields.find(f => f.name === 'G-Hiring Source');
      if (hiringSourceField && hiringSourceField.options) {
        options.hiringSources = hiringSourceField.options.choices.map(c => c.name);
      }

      const educationField = atsTable.fields.find(f => f.name === 'G Education');
      if (educationField && educationField.options) {
        options.educationLevels = educationField.options.choices.map(c => c.name);
      }

      const maritalField = atsTable.fields.find(f => f.name === 'GF-Marital');
      if (maritalField && maritalField.options) {
        options.maritalStatuses = maritalField.options.choices.map(c => c.name);
      }

      const genderField = atsTable.fields.find(f => f.name === 'G-M/F');
      if (genderField && genderField.options) {
        options.genders = genderField.options.choices.map(c => c.name);
      }

      const joinField = atsTable.fields.find(f => f.name === 'GF-Join');
      if (joinField && joinField.options) {
        options.joinTimes = joinField.options.choices.map(c => c.name);
      }

      const employmentField = atsTable.fields.find(f => f.name === 'Full/part time');
      if (employmentField && employmentField.options) {
        options.employmentTypes = employmentField.options.choices.map(c => c.name);
      }

      const uaeResidentField = atsTable.fields.find(f => f.name === 'UAE Residence?');
      if (uaeResidentField && uaeResidentField.options) {
        options.uaeResidence = uaeResidentField.options.choices.map(c => c.name);
      }

        return options;
      } catch (error) {
        console.error('Error fetching common field options:', error.response?.data || error.message);
        // Return fallback values from centralized config if API fails
        return {
          hiringSources: config.options.hiringSources,
          educationLevels: config.options.educationLevels,
          maritalStatuses: config.options.maritalStatuses,
          genders: config.options.genders.map(g => g.value),
          joinTimes: config.options.joinTimes,
          employmentTypes: config.options.employmentTypes,
          uaeResidence: config.options.uaeResidence
        };
      }
    });
  }

  /**
   * Fetch form-specific fields by form type instead of position
   * @param {string} formType - The form type (e.g., 'Tech', 'Creative', 'Digital')
   */
  async getFormFields(formType) {
    try {
      const tableName = encodeURIComponent(config.airtable.fieldsTable);
      const filterFormula = encodeURIComponent(`{Form Type}='${formType}'`);
      const url = `${this.baseUrl}/${tableName}?filterByFormula=${filterFormula}&sort%5B0%5D%5Bfield%5D=Order&sort%5B0%5D%5Bdirection%5D=asc`;

      const response = await axios.get(url, { headers: this.headers });

      return response.data.records.map(record => ({
        id: record.id,
        ...record.fields
      }));
    } catch (error) {
      console.error('Airtable fetch error (getFormFields):', error.response?.data || error.message);

      // Check if table doesn't exist
      if (error.response?.data?.error?.type === 'INVALID_PERMISSIONS_OR_MODEL_NOT_FOUND') {
        console.warn(`⚠️  Table "${config.airtable.fieldsTable}" not found or no access. Position-specific fields will be skipped.`);
      }

      return [];
    }
  }

  /**
   * Legacy method - kept for backward compatibility
   * Fetch fields by position title
   */
  async getPositionFields(positionTitle) {
    try {
      const tableName = encodeURIComponent(config.airtable.fieldsTable);
      const filterFormula = encodeURIComponent(`{Position}='${positionTitle}'`);
      const url = `${this.baseUrl}/${tableName}?filterByFormula=${filterFormula}&sort%5B0%5D%5Bfield%5D=Order&sort%5B0%5D%5Bdirection%5D=asc`;

      const response = await axios.get(url, { headers: this.headers });

      return response.data.records.map(record => ({
        id: record.id,
        ...record.fields
      }));
    } catch (error) {
      console.error('Airtable fetch error (getPositionFields):', error.response?.data || error.message);
      return [];
    }
  }

  parseFieldOptions(optionsString) {
    if (!optionsString) return [];

    try {
      return JSON.parse(optionsString);
    } catch {
      return optionsString.split(',').map(o => o.trim());
    }
  }

  /**
   * Fetch table schema including all field definitions
   * @param {string} tableName - The name of the table to fetch schema for
   */
  async getTableSchema(tableName) {
    return this.getCached(`tableSchema:${tableName}`, async () => {
      try {
        const schemaUrl = `https://api.airtable.com/v0/meta/bases/${config.airtable.baseId}/tables`;
        const schemaResponse = await axios.get(schemaUrl, { headers: this.headers });

        const table = schemaResponse.data.tables.find(t => t.name === tableName);
        if (!table) {
          console.error(`Table "${tableName}" not found in Airtable base`);
          return null;
        }

        return table;
      } catch (error) {
        console.error('Error fetching table schema:', error.response?.data || error.message);
        return null;
      }
    });
  }

  /**
   * Fetch all records from a linked table
   * @param {string} tableName - The name of the linked table
   * @param {string} displayField - The field name to use as display value
   */
  async getLinkedTableRecords(tableName, displayField = null) {
    return this.getCached(`linkedTable:${tableName}:${displayField || 'default'}`, async () => {
      try {
        const encodedTableName = encodeURIComponent(tableName);
        let allRecords = [];
        let offset = null;

        // Fetch all pages (Airtable returns max 100 per request)
        do {
          const url = `${this.baseUrl}/${encodedTableName}${offset ? `?offset=${offset}` : ''}`;
          const response = await axios.get(url, { headers: this.headers });

          allRecords = allRecords.concat(response.data.records);
          offset = response.data.offset;
        } while (offset);

        const mappedRecords = allRecords.map(record => {
          // Try to find the display field value
          let displayValue = displayField
            ? record.fields[displayField]
            : record.fields.Country || record.fields.Name || record.fields.name || record.fields.Title || record.fields.title;

          // If still no display value, use the first non-empty field
          if (!displayValue) {
            const firstField = Object.values(record.fields)[0];
            displayValue = firstField;
          }

          // Build result object
          const result = {
            id: record.id,
            value: record.id,
            label: displayValue || record.id
          };

          // For cities, include the country field for filtering
          if (record.fields.Country && Array.isArray(record.fields.Country)) {
            // Country is a linked record field, extract the first record ID
            result.country = record.fields.Country[0];
          }

          return result;
        });

        // Sort alphabetically by label
        return mappedRecords.sort((a, b) => a.label.localeCompare(b.label));
      } catch (error) {
        console.error(`Error fetching linked table records from "${tableName}":`, error.response?.data || error.message);
        return [];
      }
    });
  }

  /**
   * Get dynamic form fields configuration from table schema
   * @param {string} tableName - The name of the table to generate fields from
   */
  async getDynamicFormFields(tableName) {
    return this.getCached(`dynamicFormFields:${tableName}`, async () => {
      try {
        const tableSchema = await this.getTableSchema(tableName);
        if (!tableSchema) return [];

        const fields = [];

        for (const field of tableSchema.fields) {
          // Skip system fields
          if (field.name === 'Created' || field.name === 'Last Modified' || field.name === 'Record ID') {
            continue;
          }

          const fieldConfig = {
            id: field.id,
            name: field.name,
            fieldKey: field.name.toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, ''),
            type: this.mapAirtableTypeToFormType(field.type),
            required: false, // Airtable doesn't expose this in schema, would need to be configured separately
            description: field.description || '',
            airtableType: field.type
          };

          // Handle field-specific options
          if (field.type === 'singleSelect' || field.type === 'multipleSelects') {
            fieldConfig.options = field.options.choices.map(choice => choice.name);
          }

          // Handle linked records - fetch options from linked table
          if (field.type === 'multipleRecordLinks') {
            const linkedTableId = field.options.linkedTableId;
            // Find the linked table name from schema
            const linkedTableName = await this.getTableNameById(linkedTableId);
            if (linkedTableName) {
              fieldConfig.linkedTable = linkedTableName;
              // We'll fetch these options lazily when needed
              fieldConfig.fetchLinkedOptions = true;
            }
          }

          // Handle number fields with formatting
          if (field.type === 'number' || field.type === 'currency' || field.type === 'percent') {
            fieldConfig.numberFormat = field.options?.precision || 0;
          }

          // Handle date fields
          if (field.type === 'date' || field.type === 'dateTime') {
            fieldConfig.includeTime = field.type === 'dateTime';
          }

          fields.push(fieldConfig);
        }

        return fields;
      } catch (error) {
        console.error('Error generating dynamic form fields:', error.response?.data || error.message);
        return [];
      }
    });
  }

  /**
   * Get table name by table ID
   * @param {string} tableId - The Airtable table ID
   */
  async getTableNameById(tableId) {
    try {
      const schemaUrl = `https://api.airtable.com/v0/meta/bases/${config.airtable.baseId}/tables`;
      const schemaResponse = await axios.get(schemaUrl, { headers: this.headers });

      const table = schemaResponse.data.tables.find(t => t.id === tableId);
      return table ? table.name : null;
    } catch (error) {
      console.error('Error fetching table name:', error.response?.data || error.message);
      return null;
    }
  }

  /**
   * Map Airtable field types to form input types
   * @param {string} airtableType - The Airtable field type
   */
  mapAirtableTypeToFormType(airtableType) {
    const typeMap = {
      'singleLineText': 'text',
      'multilineText': 'textarea',
      'email': 'email',
      'url': 'url',
      'number': 'number',
      'currency': 'number',
      'percent': 'number',
      'singleSelect': 'select',
      'multipleSelects': 'multiselect',
      'checkbox': 'checkbox',
      'date': 'date',
      'dateTime': 'datetime',
      'phoneNumber': 'tel',
      'multipleRecordLinks': 'select', // Linked records as select dropdown
      'multipleAttachments': 'file',
      'rating': 'number',
      'duration': 'number'
    };

    return typeMap[airtableType] || 'text';
  }
}

const airtableService = new AirtableService();
export default airtableService;